// ts-rest contract for the suitability refresh of an existing client (SEP-288
// sections 3 to 5).
//
// Endpoints under /api/suitability-refresh/*:
//   GET  /api/suitability-refresh/by-version/:versionId   — the refresh of one SUI
//   GET  /api/suitability-refresh/queue                   — the Compliance queue
//   POST /api/suitability-refresh/:refreshId/issued       — the fact find went to the client
//   POST /api/suitability-refresh/:refreshId/checklist    — save the checklist and the outcome
//   POST /api/suitability-refresh/:refreshId/submit       — submit the outcome to Compliance
//   POST /api/suitability-refresh/:refreshId/decision     — Compliance decides the outcome
//   POST /api/suitability-refresh/:refreshId/mandate      — record the new mandate (Outcome B)
//   POST /api/suitability-refresh/:refreshId/mandate-decision — Compliance decides the mandate
//   POST /api/suitability-refresh/:refreshId/letter/draft  — draft the Outcome A letter
//   POST /api/suitability-refresh/:refreshId/letter        — save the edited letter
//   POST /api/suitability-refresh/:refreshId/letter/outlook-draft — an Outlook draft of it
//   POST /api/suitability-refresh/:refreshId/letter/sent   — record the send
//   GET  /api/suitability-refresh/:refreshId/letter/docx   — the Word file (a plain Nest route)
//   GET  /api/suitability-refresh/:refreshId/change-summary — the changed answers and their summary
//   POST /api/suitability-refresh/:refreshId/change-summary — queue the SCRIVENER summary (SEP-351)

import { initContract } from '@ts-rest/core';
import { z } from 'zod';

import { errorBodyWithCode as errorBody } from '../schemas/common';
import {
    refreshChangeSummarySchema,
    refreshChecklistSchema,
    suitabilityRefreshOutcomeSchema,
    suitabilityRefreshStatusSchema,
} from '../suitability-refresh';

const c = initContract();

const decisionSchema = z.enum(['approved', 'rejected']);

export const suitabilityRefreshSchema = z.object({
    refreshId: z.string(),
    clientId: z.string(),
    clientName: z.string(),
    factFindId: z.string(),
    versionId: z.string(),
    suiReference: z.string().nullable(),
    /** The person of the SUI. Null when it names the client alone. */
    subjectName: z.string().nullable(),
    /** The SUI that the refresh replaces, and its number. */
    previousVersionId: z.string().nullable(),
    previousReference: z.string().nullable(),
    suitabilityRecordId: z.string().nullable(),
    /** The status of the fact-find version of the refresh. */
    versionStatus: z.string(),
    status: suitabilityRefreshStatusSchema,
    issuedAt: z.string().nullable(),
    returnedAt: z.string().nullable(),
    reminder1At: z.string().nullable(),
    reminder2At: z.string().nullable(),
    escalatedAt: z.string().nullable(),
    outcome: suitabilityRefreshOutcomeSchema.nullable(),
    outcomeSummary: z.string().nullable(),
    outcomeBy: z.string().nullable(),
    outcomeAt: z.string().nullable(),
    checklist: refreshChecklistSchema.nullable(),
    complianceDecision: decisionSchema.nullable(),
    complianceBy: z.string().nullable(),
    complianceAt: z.string().nullable(),
    complianceComment: z.string().nullable(),
    mandateLetterId: z.string().nullable(),
    mandateDecision: decisionSchema.nullable(),
    mandateBy: z.string().nullable(),
    mandateAt: z.string().nullable(),
    mandateComment: z.string().nullable(),
    letterBody: z.string().nullable(),
    changeSummary: z.string().nullable(),
    letterSentAt: z.string().nullable(),
    letterSentTo: z.string().nullable(),
    createdAt: z.string(),
});
export type SuitabilityRefreshDto = z.infer<typeof suitabilityRefreshSchema>;

const refreshBody = z.object({ refresh: suitabilityRefreshSchema });
const refreshOrNull = z.object({ refresh: suitabilityRefreshSchema.nullable() });

export const saveRefreshChecklistRequestSchema = z.object({
    checklist: refreshChecklistSchema,
    outcome: suitabilityRefreshOutcomeSchema.nullable(),
    outcomeSummary: z.string().nullable(),
});
export type SaveRefreshChecklistRequest = z.infer<typeof saveRefreshChecklistRequestSchema>;

export const refreshDecisionRequestSchema = z.object({
    decision: decisionSchema,
    comment: z.string().nullable(),
});
export type RefreshDecisionRequest = z.infer<typeof refreshDecisionRequestSchema>;

export const suitabilityRefreshContract = c.router(
    {
        getRefreshByVersion: {
            method: 'GET',
            path: '/suitability-refresh/by-version/:versionId',
            responses: { 200: refreshOrNull, 404: errorBody },
            summary: 'The refresh of one SUI, or null when the SUI is not a refresh',
        },
        listRefreshQueue: {
            method: 'GET',
            path: '/suitability-refresh/queue',
            query: z.object({ status: z.string().optional() }).optional(),
            responses: { 200: z.object({ rows: z.array(suitabilityRefreshSchema) }) },
            summary: 'The refreshes that wait for Compliance, newest submission first',
        },
        markRefreshIssued: {
            method: 'POST',
            path: '/suitability-refresh/:refreshId/issued',
            body: z.object({}),
            responses: { 200: refreshBody, 403: errorBody, 404: errorBody, 409: errorBody },
            summary: 'Record that the fact find went to the client; the reminders start',
        },
        saveRefreshChecklist: {
            method: 'POST',
            path: '/suitability-refresh/:refreshId/checklist',
            body: saveRefreshChecklistRequestSchema,
            responses: { 200: refreshBody, 403: errorBody, 404: errorBody, 409: errorBody },
            summary: 'Save the Internal Suitability Refresh Checklist and the chosen outcome',
        },
        submitRefreshOutcome: {
            method: 'POST',
            path: '/suitability-refresh/:refreshId/submit',
            body: saveRefreshChecklistRequestSchema,
            responses: { 200: refreshBody, 403: errorBody, 404: errorBody, 409: errorBody },
            summary: 'Submit the outcome to Compliance; the checklist must be complete',
        },
        decideRefresh: {
            method: 'POST',
            path: '/suitability-refresh/:refreshId/decision',
            body: refreshDecisionRequestSchema,
            responses: { 200: refreshBody, 404: errorBody, 409: errorBody },
            summary: 'Compliance approves or rejects the refresh outcome',
        },
        recordRefreshMandate: {
            method: 'POST',
            path: '/suitability-refresh/:refreshId/mandate',
            body: z.object({ mandateLetterId: z.string().uuid() }),
            responses: { 200: refreshBody, 403: errorBody, 404: errorBody, 409: errorBody },
            summary: 'Record the new mandate of an Outcome B refresh for Compliance approval',
        },
        decideRefreshMandate: {
            method: 'POST',
            path: '/suitability-refresh/:refreshId/mandate-decision',
            body: refreshDecisionRequestSchema,
            responses: { 200: refreshBody, 404: errorBody, 409: errorBody },
            summary: 'Compliance approves or rejects the new mandate of an Outcome B refresh',
        },
        // SEP-351. The changed answers of the refresh fact find against the
        // last completed fact find. Null when the refresh replaces no SUI.
        getRefreshChangeSummary: {
            method: 'GET',
            path: '/suitability-refresh/:refreshId/change-summary',
            responses: {
                200: z.object({ changeSummary: refreshChangeSummarySchema.nullable() }),
                403: errorBody,
                404: errorBody,
            },
            summary: 'The changed answers of a refresh fact find, and the newest summary of them',
        },
        // The summary runs as a governed SCRIVENER AgentJob, so it answers 202
        // and the caller reads the result back from the GET endpoint above.
        runRefreshChangeSummary: {
            method: 'POST',
            path: '/suitability-refresh/:refreshId/change-summary',
            body: z.object({}),
            responses: {
                202: z.object({ jobId: z.string() }),
                403: errorBody,
                404: errorBody,
                409: errorBody,
            },
            summary: 'Queue a SCRIVENER summary of the changed answers of a refresh fact find',
        },
        draftRefreshLetter: {
            method: 'POST',
            path: '/suitability-refresh/:refreshId/letter/draft',
            body: z.object({}),
            responses: { 200: refreshBody, 403: errorBody, 404: errorBody, 409: errorBody },
            summary: 'Draft the Outcome A confirmation letter, with the summary of changes',
        },
        saveRefreshLetter: {
            method: 'POST',
            path: '/suitability-refresh/:refreshId/letter',
            body: z.object({ letterBody: z.string().min(1) }),
            responses: { 200: refreshBody, 403: errorBody, 404: errorBody, 409: errorBody },
            summary: 'Save the confirmation letter as the IM or the EA edited it',
        },
        refreshLetterOutlookDraft: {
            method: 'POST',
            path: '/suitability-refresh/:refreshId/letter/outlook-draft',
            body: z.object({}),
            responses: {
                200: z.object({ webUrl: z.string(), to: z.string().nullable() }),
                400: errorBody,
                403: errorBody,
                404: errorBody,
                409: errorBody,
            },
            summary: 'Create an Outlook draft of the approved letter, with the Word file attached',
        },
        markRefreshLetterSent: {
            method: 'POST',
            path: '/suitability-refresh/:refreshId/letter/sent',
            body: z.object({ sentTo: z.string().min(1) }),
            responses: { 200: refreshBody, 403: errorBody, 404: errorBody, 409: errorBody },
            summary: 'Record that the confirmation letter went to the client',
        },
    },
    { pathPrefix: '/api', strictStatusCodes: false },
);
